import { ToolCallData } from "../../stores";
import { getToolDisplayInfo } from "../../toolConfig";
import { ToolCallStatus, ToolName } from "../../types";
import { parseEntityPreview } from "../../utils/entityPreview";
import { PulseDot } from "../PulseDot";

import { AnimatedToolText } from "./AnimatedToolText";
import { EntityPreviewCard } from "./EntityPreviewCard";
import {
  TOOL_CALL_WIDGET_FADE_OUT_MS,
  TOOL_CALL_WIDGET_ICON_SHIMMER_MS,
} from "./toolWidgetAnimationConstants";
import { ToolIcon } from "./ToolIcon";
import { ToolNavigationButtons } from "./ToolNavigationButtons";
import { ToolResultLinkRow } from "./ToolResultLinkRow";
import { ToolText } from "./ToolText";

interface ToolCallWidgetProps {
  toolCall: ToolCallData;
  hideNavigation?: boolean;
  useNavigationRow?: boolean;
}

export const ToolCallWidget = ({
  toolCall,
  hideNavigation = false,
  useNavigationRow = false,
}: ToolCallWidgetProps) => {
  const { status, navigation } = toolCall;
  const displayInfo = getToolDisplayInfo(toolCall);
  const isComplete = status === ToolCallStatus.COMPLETED;
  const isError = status === ToolCallStatus.ERROR;
  const isFailed = status === ToolCallStatus.FAILED;

  if (isComplete) {
    const navigationItems = navigation && navigation.length > 0 ? navigation : undefined;
    const hasNavigation = !hideNavigation && !!navigationItems;
    const entityPreview = parseEntityPreview(toolCall);
    const shouldRenderNavigationRow =
      !!navigationItems &&
      (!displayInfo.shouldHideWhenComplete || useNavigationRow) &&
      (!hideNavigation || useNavigationRow);

    if (entityPreview) {
      return (
        <div
          className="my-0.5 transition-opacity ease-out"
          style={{ transitionDuration: `${TOOL_CALL_WIDGET_FADE_OUT_MS}ms` }}
        >
          <EntityPreviewCard preview={entityPreview} />
        </div>
      );
    }

    if (displayInfo.shouldHideWhenComplete && !navigationItems) {
      return null;
    }

    if (shouldRenderNavigationRow) {
      const NavigationContent =
        toolCall.name === ToolName.RETURN_RESULTS ? (
          <ToolNavigationButtons navigation={navigationItems} />
        ) : (
          <ToolResultLinkRow displayInfo={displayInfo} navigation={navigationItems} />
        );

      return (
        <div
          className="my-0.5 transition-opacity ease-out"
          style={{ transitionDuration: `${TOOL_CALL_WIDGET_FADE_OUT_MS}ms` }}
        >
          {NavigationContent}
        </div>
      );
    }

    return (
      <div
        className="my-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 transition-opacity ease-out"
        style={{ transitionDuration: `${TOOL_CALL_WIDGET_FADE_OUT_MS}ms` }}
      >
        {!displayInfo.shouldHideWhenComplete && (
          <div className="flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center">
              <ToolIcon type={displayInfo.type} className="text-gray-400" />
            </span>
            <ToolText className="text-gray-500">{displayInfo.displayName}</ToolText>
          </div>
        )}
        {hasNavigation && <ToolNavigationButtons navigation={navigation} />}
      </div>
    );
  }

  if (isFailed) {
    return (
      <div className="my-0.5">
        <div className="flex items-center gap-1">
          <PulseDot variant="gray" className="m-0.5" />
          <ToolText className="text-gray-500">{displayInfo.displayName}</ToolText>
          <ToolText className="text-gray-400">— cancelled</ToolText>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="my-0.5">
        <div className="flex items-center gap-1">
          <PulseDot variant="gray" className="m-0.5" />
          <ToolText className="text-gray-500">{displayInfo.displayName}</ToolText>
        </div>
      </div>
    );
  }

  return (
    <div
      className="my-0.5 flex items-center gap-2 transition-opacity ease-out"
      style={{ transitionDuration: `${TOOL_CALL_WIDGET_FADE_OUT_MS}ms` }}
    >
      <span className="relative flex h-5 w-5 items-center justify-center overflow-hidden rounded-md bg-blue-50 ring-1 ring-blue-100">
        <span
          className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent"
          style={{ animationDuration: `${TOOL_CALL_WIDGET_ICON_SHIMMER_MS}ms` }}
        />
        <ToolIcon type={displayInfo.type} isAnimated />
      </span>
      <AnimatedToolText>{displayInfo.displayName}</AnimatedToolText>
    </div>
  );
};

export default ToolCallWidget;
